import request from "@/utils/request";
import { BASE_URL } from "@/utils/request";

const customerInfoCache = new Map();
const customerInfoPromiseCache = new Map();

export function extractCustomerPayload(response) {
  if (!response || typeof response !== "object") {
    return null;
  }

  if (response.data && typeof response.data === "object" && !Array.isArray(response.data)) {
    return response.data;
  }

  if (
    response.data &&
    response.data.data &&
    typeof response.data.data === "object" &&
    !Array.isArray(response.data.data)
  ) {
    return response.data.data;
  }

  if (response.result && typeof response.result === "object" && !Array.isArray(response.result)) {
    return response.result;
  }

  if (response.customerNo || response.customer_no || response.customerNO) {
    return response;
  }

  return null;
}

export function extractCustomerNo(response) {
  const payload = extractCustomerPayload(response);
  if (!payload) {
    return "";
  }

  return (
    payload.customerNo ||
    payload.customer_no ||
    payload.customerNO ||
    payload.customerId ||
    payload.customer_id ||
    ""
  );
}

function buildCustomerInfoCacheKey(params = {}) {
  return JSON.stringify({
    corpId: params.corpId || "",
    customerUserId: params.customerUserId || "",
    serUserId: params.serUserId || "",
  });
}

// 获取企业签名 (使用fetch，用于SDK注册)
export async function fetchCorpSign(corpId, url) {
  const encodedUrl = encodeURIComponent(url);
  const apiUrl = `${BASE_URL}/wechat/cp/jsapi/getCorpSign?corpId=${corpId}&url=${encodedUrl}`;
  const response = await fetch(apiUrl);
  if (!response.ok) {
    throw new Error(`企业配置签名请求失败! 状态码: ${response.status}`);
  }
  return response.json();
}

// 获取应用签名 (使用fetch，用于SDK注册)
export async function fetchAgentSign(corpId, agentId, url) {
  const encodedUrl = encodeURIComponent(url);
  const apiUrl = `${BASE_URL}/wechat/cp/jsapi/getAgentSign?corpId=${corpId}&agentId=${agentId}&url=${encodedUrl}`;
  const response = await fetch(apiUrl);
  if (!response.ok) {
    throw new Error(`应用配置签名请求失败! 状态码: ${response.status}`);
  }
  return response.json();
}

// 获取当前登录用户ID
export function getUserIds(params) {
  return request({
    url: "/wechat/cp/auth/getUserId",
    method: "get",
    params,
  });
}

// 获取客户信息（带缓存，只调用一次）
export function getCustomerInfo(params) {
  const cacheKey = buildCustomerInfoCacheKey(params);

  // 如果已有缓存数据，直接返回
  if (customerInfoCache.has(cacheKey)) {
    return Promise.resolve(customerInfoCache.get(cacheKey));
  }

  // 如果正在请求中，返回同一个 Promise
  if (customerInfoPromiseCache.has(cacheKey)) {
    return customerInfoPromiseCache.get(cacheKey);
  }

  // 发起请求并缓存 Promise
  const requestPromise = request({
    url: "/api/v2/qw/sidebar/customer/info",
    method: "get",
    params,
  })
    .then((response) => {
      // 请求成功后缓存结果
      customerInfoCache.set(cacheKey, response);
      customerInfoPromiseCache.delete(cacheKey);
      
      // 同时存储 customerNo 到 localStorage
      const customerNo = extractCustomerNo(response);
      if (response && response.code === 200 && customerNo) {
        localStorage.setItem("wechat_customerNo", customerNo);
        console.log("✓ 保存customerNo到本地存储:", customerNo);
      }
      
      return response;
    })
    .catch((error) => {
      // 请求失败时清除 Promise 缓存，允许重试
      customerInfoPromiseCache.delete(cacheKey);
      throw error;
    });

  customerInfoPromiseCache.set(cacheKey, requestPromise);

  return requestPromise;
}

// 清除客户信息缓存（用于需要重新获取的场景）
export function clearCustomerInfoCache() {
  customerInfoCache.clear();
  customerInfoPromiseCache.clear();
  // 同时清除 localStorage 中的 customerNo
  localStorage.removeItem("wechat_customerNo");
}

// 获取订单列表
export function getOrderList(customerNo, pageNum = 1, pageSize = 5, externalUserId = "", corpId = "", userId = "") {
  return request({
    url: "/api/v2/qw/sidebar/wx-order/list",
    method: "get",
    params: {
      customerNo,
      pageNum,
      pageSize,
      externalUserId,
      corpId,
      userId,
    },
  });
}

// 获取订单详情
export function desensitization(orderNo) {
  return request({
    url: `/api/v2/qw/sidebar/wx-order/info`,
    method: "get",
    params: {
      orderNo,
    },
  });
}

// 订单绑定
export function linkOrder(data) {
  return request({
    url: "/api/v2/qw/sidebar/order/link",
    method: "post",
    data,
  });
}

// 获取物流信息
export function getLogisticsInfo(orderNo) {
  return request({
    url: "/api/v2/qw/sidebar/sys-order/list",
    method: "get",
    params: {
      orderNo,
    },
  });
}
// 客户地址列表
export function receiverList(customerNo) {
  return request({
    url: "/api/v2/qw/sidebar/receiver/list",
    method: "get",
    params: {
      customerNo,
    },
  });
}
// 代客下单商品列表
export function selectReplaceGoodslist(params) {
  return request({
    url: "/api/v2/qw/sidebar/replace/list",
    method: "get",
    params,
  });
}
// 代客下单
export function replaceTheOrder(data) {
  return request({
    url: "/api/v2/qw/sidebar/replace-order",
    method: "post",
    data,
  });
}

// 预设下单
export function savePreOrder(data) {
  return request({
    url: "/api/v2/qw/sidebar/order-pre/save",
    method: "post",
    data,
  });
}

// 预订单列表
export function getPreOrderList(customerNo) {
  return request({
    url: "/api/v2/qw/sidebar/order-pre/list",
    method: "get",
    params: {
      customerNo,
    },
  });
}

// 更新预订单
export function updatePreOrder(data) {
  return request({
    url: "/api/v2/qw/sidebar/order-pre/update",
    method: "post",
    data,
  });
}
// 三级省市区
export function getareaLevel3() {
  return request({
    url: "/api/busi/custom/areas/level3",
    method: "get",
  });
}

// 添加联系客户提醒
export function addLinkAlert(data) {
  return request({
    url: "/api/v2/qw/sidebar/add-link-alert",
    method: "post",
    data,
  });
}

// 新增收货地址
export function addReceiver(data) {
  return request({
    url: "/api/v2/qw/sidebar/add/receive-info",
    method: "post",
    data,
  });
}

// 上传图片
export function uploadImage(file) {
  const data = new FormData();
  data.append("file", file);

  return request({
    url: "/api/common/upload",
    method: "post",
    data,
  });
}

// 客户标签 - 可选标签(只返回已启用的全局标签)
export function getCustomerTagOptions(params) {
  return request({
    url: "/api/v2/qw/sidebar/customer/tag/options",
    method: "get",
    params,
  });
}

// 客户标签 - 当前已分配标签(包含已禁用/已删除的标签，用于展示)
export function getCustomerTagList(params) {
  return request({
    url: "/api/v2/qw/sidebar/customer/tag/list",
    method: "get",
    params,
  });
}

// 客户标签 - 覆盖式更新
export function updateCustomerTags(data) {
  return request({
    url: "/api/v2/qw/sidebar/customer/tag",
    method: "put",
    data,
  });
}
